import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import { useNavigate } from "react-router-dom";
import { Button, InputAdornment } from "@mui/material";
import PreloaderTable from "../../lib/components/PreloaderTable";
import { Add, Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import UsersDataTable from "./components/users-data-table";
import scrollHandler from "../../utils/scrollHandler";
import userservice from "../../service/userservice";
import useDebounce from "../../hooks/useDebounce";
import AppTab from "../../components/tab-label";
import AddNewUser from "./components/AddUser";
import AppExportButton from "../../lib/components/AppExportButton";
import AppExportModal, { EXPORT_HEADER_OPTIONS } from "../../lib/components/AppExportModal";

const tabList = [{ label: "All", value: "ALL" }];

const Users = () => {
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "";
  const searchTerm = useDebounce(keyword);

  const [isAddNew, setIsAddNew] = React.useState(false);
  const [isExportOpen, setIsExportOpen] = React.useState(false);

  const queries = { page, limit, keyword: searchTerm, status };

  const usersQuery = useQuery({
    retry: false,
    queryKey: ["subscribers", ...Object.values(queries)],
    queryFn: () => userservice.find({ ...queries }),
  });

  const { isPending: isLoading, data } = usersQuery;

  const usersData = data?.data;
  return (
    <AppLayout>
      <Header
        title="Users"
        description="Manage who has access to what on Jureb"
      />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className="d-flex mb-5 justify-content-between align-items-center gap-3">
          <div className="flex-grow-1" style={{ maxWidth: 420 }}>
            <AppTextField
              placeholder="Search users..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <AppExportButton onClick={() => setIsExportOpen(true)} />
        </div>
        <div className="d-flex justify-content-between align-items-center gap-4 mb-4">
          <AppTab
            items={tabList}
            onChange={handleChangeTab}
            value={status || "ALL"}
            statsMap={{ all: usersData?.totalDocs }}
          />
          <Button
            onClick={() => setIsAddNew(true)}
            endIcon={<Add />}
            variant="contained"
            color="secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Add new user
          </Button>
        </div>
        <PreloaderTable isLoading={isLoading} />
        <UsersDataTable
          isLoading={isLoading}
          data={usersData?.data}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={usersData?.totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) =>
            handleChange({ target: { value } }, "limit")
          }
        />
      </div>
      <AddNewUser
        isOpen={isAddNew}
        onClose={() => setIsAddNew(false)}
        refetch={() => usersQuery.refetch()}
      />
      <AppExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        headerOptions={EXPORT_HEADER_OPTIONS.users}
        showDateRange
        defaultFilters={{ keyword: searchTerm }}
        onExport={(params) => userservice.export(params)}
      />
    </AppLayout>
  );

  function handleChangeTab(_, value) {
    searchParams.set("plan", value);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }

  function handleChange(e, name = "") {
    const value = name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 },
    );
    if (name === "page") scrollHandler();
  }
};

export default Users;
